import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  Zap, 
  Upload, 
  BookOpen, 
  Clock, 
  BarChart3,
  Settings,
  LogOut,
  FileText,
  Trash2,
  Play,
  Sun,
  Moon,
  Menu,
  X,
  Plus,
  BookMarked,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Gradient covers for documents
const gradientCovers = [
  'gradient-cover-1',
  'gradient-cover-2',
  'gradient-cover-3',
  'gradient-cover-4',
];

function getGradientClass(index) {
  return gradientCovers[index % gradientCovers.length];
}

function formatFileType(type) {
  return type.toUpperCase();
}

function formatDate(dateStr) {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  });
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  const [documents, setDocuments] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    }
  }, []);

  const fetchStatistics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/statistics`);
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchDocuments(), fetchStatistics()]).finally(() => setLoading(false));
  }, [fetchDocuments, fetchStatistics]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await uploadFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = async (e) => {
    if (e.target.files && e.target.files[0]) {
      await uploadFile(e.target.files[0]);
    }
  };

  const uploadFile = async (file) => {
    const allowedTypes = ['.pdf', '.epub', '.txt'];
    const extension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(extension)) {
      toast.error('Please upload a PDF, EPUB, or TXT file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully!');
      setUploadOpen(false);
      fetchDocuments();
      fetchStatistics();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload document';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (docId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success('Document deleted');
      fetchDocuments();
      fetchStatistics();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const reprocessDocument = async (docId, e) => {
    e.stopPropagation();
    toast.info('Reprocessing document...');
    
    try {
      await axios.post(`${API}/documents/${docId}/reprocess`);
      toast.success('Document reprocessed successfully! Text extraction improved.');
      fetchDocuments();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('re-upload')) {
        toast.error('This document was uploaded before reprocessing was available. Please delete and re-upload the file to use improved text extraction.');
      } else {
        toast.error(detail || 'Failed to reprocess document');
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const stats = [
    { 
      label: 'Documents', 
      value: statistics?.total_documents || 0, 
      icon: BookOpen,
      color: 'text-primary'
    },
    { 
      label: 'Words Read', 
      value: (statistics?.total_words_read || 0).toLocaleString(), 
      icon: FileText,
      color: 'text-accent'
    },
    { 
      label: 'Reading Time', 
      value: `${statistics?.total_reading_time_minutes || 0}m`, 
      icon: Clock,
      color: 'text-secondary'
    },
    { 
      label: 'Avg. WPM', 
      value: statistics?.average_wpm || 0, 
      icon: Zap,
      color: 'text-primary'
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Zap className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold">SpeedRead</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border transform transition-transform duration-300
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="hidden lg:flex items-center gap-2 px-6 py-5 border-b border-border">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Zap className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-lg">SpeedRead</span>
          </div>

          {/* User Info */}
          <div className="px-6 py-4 border-b border-border mt-14 lg:mt-0">
            <p className="font-medium truncate">{user?.name}</p>
            <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1">
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-3"
              onClick={() => {
                navigate('/dashboard');
                setSidebarOpen(false);
              }}
              data-testid="nav-library-btn"
            >
              <BookOpen className="w-5 h-5" />
              Library
            </Button>
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-3"
              onClick={() => {
                navigate('/statistics');
                setSidebarOpen(false);
              }}
              data-testid="nav-statistics-btn"
            >
              <BarChart3 className="w-5 h-5" />
              Statistics
            </Button>
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-3"
              onClick={() => {
                navigate('/settings');
                setSidebarOpen(false);
              }}
              data-testid="nav-settings-btn"
            >
              <Settings className="w-5 h-5" />
              Settings
            </Button>
          </nav>

          {/* Bottom Actions */}
          <div className="px-4 py-4 border-t border-border space-y-1">
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-3"
              onClick={toggleTheme}
              data-testid="sidebar-theme-toggle"
            >
              {theme === 'dark' ? (
                <>
                  <Sun className="w-5 h-5" />
                  Light Mode
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5" />
                  Dark Mode
                </>
              )}
            </Button>
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-3 text-destructive hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8 max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading font-bold text-2xl lg:text-3xl mb-1">
                Your Library
              </h1>
              <p className="text-muted-foreground">
                {documents.length} document{documents.length !== 1 ? 's' : ''} in your collection
              </p>
            </div>
            <Button 
              onClick={() => setUploadOpen(true)}
              className="btn-pill gap-2"
              data-testid="upload-btn"
            >
              <Plus className="w-4 h-4" />
              Upload Document
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-xl p-4 border border-border"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center ${stat.color}`}>
                    <stat.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Documents Grid */}
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-card rounded-xl p-6 border border-border animate-pulse">
                  <div className="h-24 bg-muted rounded-lg mb-4" />
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : documents.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
                <BookMarked className="w-10 h-10 text-muted-foreground" />
              </div>
              <h3 className="font-heading font-semibold text-xl mb-2">No documents yet</h3>
              <p className="text-muted-foreground mb-6">
                Upload your first PDF, EPUB, or text file to start speed reading
              </p>
              <Button 
                onClick={() => setUploadOpen(true)}
                className="btn-pill gap-2"
                data-testid="empty-upload-btn"
              >
                <Upload className="w-4 h-4" />
                Upload Your First Document
              </Button>
            </motion.div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {documents.map((doc, index) => (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/read/${doc.id}`)}
                  className="group bg-card rounded-xl border border-border overflow-hidden cursor-pointer card-hover"
                  data-testid={`document-card-${doc.id}`}
                >
                  {/* Cover */}
                  <div className={`h-28 ${getGradientClass(index)} flex items-center justify-center relative`}>
                    <span className="text-white/90 font-reader text-2xl font-medium">
                      {formatFileType(doc.file_type)}
                    </span>
                    
                    {/* Actions */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            variant="secondary" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`document-menu-${doc.id}`}
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="6" r="2" />
                              <circle cx="12" cy="12" r="2" />
                              <circle cx="12" cy="18" r="2" />
                            </svg>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/read/${doc.id}`)}>
                            <Play className="w-4 h-4 mr-2" />
                            Read Now
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => reprocessDocument(doc.id, e)}>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reprocess Text
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={(e) => deleteDocument(doc.id, e)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <h3 className="font-medium truncate mb-1" title={doc.title}>
                      {doc.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      {doc.word_count.toLocaleString()} words
                    </p>
                    
                    {/* Progress */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="text-foreground font-medium">
                          {Math.round(doc.progress_percent)}%
                        </span>
                      </div>
                      <Progress value={doc.progress_percent} className="h-1.5" />
                    </div>
                    
                    {doc.last_read_at && (
                      <p className="text-xs text-muted-foreground mt-3">
                        Last read: {formatDate(doc.last_read_at)}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>
              Upload a PDF, EPUB, or TXT file to start speed reading
            </DialogDescription>
          </DialogHeader>
          
          <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`
              mt-4 border-2 border-dashed rounded-xl p-8 text-center transition-colors
              ${dragActive ? 'border-primary bg-primary/5' : 'border-border'}
              ${uploading ? 'opacity-50 pointer-events-none' : ''}
            `}
          >
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Upload className="w-8 h-8 text-muted-foreground" />
            </div>
            
            <p className="font-medium mb-1">
              {uploading ? 'Uploading...' : 'Drop your file here'}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              or click to browse
            </p>
            
            <input
              type="file"
              accept=".pdf,.epub,.txt"
              onChange={handleFileInput}
              className="hidden"
              id="file-upload"
              disabled={uploading}
              data-testid="file-input"
            />
            <label htmlFor="file-upload">
              <Button 
                asChild 
                variant="outline" 
                className="btn-pill"
                disabled={uploading}
              >
                <span data-testid="browse-files-btn">Browse Files</span>
              </Button>
            </label>
            
            <p className="text-xs text-muted-foreground mt-4">
              Supported: PDF, EPUB, TXT
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
